import { ActionEvents, WebActionEvents } from '@griply/types';
import { countedGoalIds, taskTypeToTrackingTaskType } from '@griply/utils';
import { useCallback } from 'react';
import { useUpdateGoalTaskCountMutation } from 'shared/hooks/queries/use-update-goal-task-count-mutation';
import { useUpdateTaskMutation } from 'shared/hooks/queries/use-update-task-mutation';
import { useRecordPmfActivity } from 'shared/hooks/use-record-pmf-activity';
import { useTrackEvents } from 'shared/hooks/use-track-events';
import { Task } from 'shared/types/task';
import { TaskType } from 'shared/types/task-base';
import { Timestamp } from 'shared/types/timestamp';

// Whole-habit-complete semantic — toggles `habit.completedAt` directly to mark the habit
// done forever (web-only "Complete habit" dialog button). For per-day task/habit completion
// from rows, menus, and the calendar, use `useTaskTodayControls().onComplete` instead.
export const useCompleteTask = () => {
  const { mutate: updateTask } = useUpdateTaskMutation();
  const { mutate: updateGoalTaskCount } = useUpdateGoalTaskCountMutation();
  const track = useTrackEvents();
  const { recordCompletion } = useRecordPmfActivity();

  return useCallback(
    (task: Task, date: Timestamp = new Date()) => {
      const taskToUpdate = { ...task };
      taskToUpdate.completedAt = taskToUpdate.completedAt ? null : date;

      if (taskToUpdate.completedAt) {
        [TaskType.Habit, TaskType.Repeating].includes(taskToUpdate.type)
          ? track(WebActionEvents.HabitComplete, undefined)
          : track(ActionEvents.TaskComplete, {
              type: taskTypeToTrackingTaskType[taskToUpdate.type],
            });
        recordCompletion();
      }

      updateTask(taskToUpdate);

      countedGoalIds(taskToUpdate).forEach((goalId) =>
        updateGoalTaskCount({
          goalId,
          completedTaskCount: taskToUpdate.completedAt ? 1 : -1,
        }),
      );

      return taskToUpdate;
    },
    [track, recordCompletion, updateTask, updateGoalTaskCount],
  );
};
